import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { m, sceneScale } from './site';

// Set dressing that works WITH Google's photoreal tiles rather than instead of
// them: instanced planting in the near field, and a distant dune range on the
// horizon.
//
// This split is the whole trick, and it took three attempts to find. Sand and
// photoreal terrain cannot share ground: an apron ends in a visible seam
// wherever the tiles show past it, and box massing reads as cubes floating on
// imagery. But they never have to share. The tiles are convincing from the plot
// boundary out to a few kilometres and that is exactly where they are left
// alone; the dunes start beyond their useful range and only ever appear as a
// silhouette above the horizon line. Nothing overlaps, so there is no seam to
// hide.
//
// The backdrop is deliberately unlit and self-hazed, its colour driven from the
// scene's own fog so it tracks dawn through night without ever separating from
// the sky it sits against. Depth of field finishes the job: at that distance it
// is always well outside the focal plane, so it reads as soft distance rather
// than as geometry.
//
// Everything is authored in METRES through m(), so it follows sceneScale rather
// than the maquette's arbitrary units.

// ---- Distant dune range --------------------------------------------------
// Sized from what the tiles actually cover, not from a guess.
//
// Measured: loaded tile geometry reaches about 3 km from the site. The first
// version started the ring at 6 km, which left a 3 km hole of nothing between
// the terrain and the dunes, and gave the range a top just 158 m above grade at
// that distance: 1.5 degrees of arc, in a tone 14% off the haze. It rendered
// perfectly and was invisible.
//
// So it now starts where the terrain stops. The relief ramps from zero at the
// inner rim, so any overlap with tiles sits at ground level and hides behind
// them; the range only rises once it is clear of the photoreal ground.
const DUNE_INNER_M = 3000;
const DUNE_RAMP_M = 3500;
const DUNE_OUTER_M = 30000;
const DUNE_RADIAL_SEGMENTS = 260;
const DUNE_RINGS = 14;
// Big enough to read from kilometres away: ~420 m of relief starting at 2.6 km
// subtends around 8 degrees, which is a skyline rather than a sliver.
const DUNE_RELIEF_M = 420;
// Prevailing wind, which gives the range its grain rather than random lumps.
const DUNE_BEARING_RAD = THREE.MathUtils.degToRad(28);

// How far the backdrop's own tone sits from the haze it stands in. Near zero
// and the range vanishes; too far and it reads as a cardboard cutout.
const BACKDROP_CONTRAST = 0.2;

// ---- Desert houses -------------------------------------------------------
// Clustered, not scattered: At-Turaif is walled compounds, and evenly spread
// boxes were the clearest tell when this was first tried. Each is seated on the
// real terrain by raycast rather than assumed flat, which is what stops them
// reading as cubes floating on satellite imagery.
const TOWN_CLUSTERS = 190;
const TOWN_BLOCKS_PER_CLUSTER = 13;
const TOWN_INNER_M = 380;
// Kept inside DUNE_INNER_M so the settlement reads in front of the range rather
// than punching through it, and inside the ~3 km the tiles actually cover: past
// that there is no ground at all and the outer compounds would stand on nothing.
const TOWN_OUTER_M = 2900;
// Massing grows with distance, from low courtyard houses on the near ring to
// full city blocks on the far one. Distance alone does not add depth here,
// because everything out there is soft with haze and depth of field: at 2.5 km a
// 9 m villa is a smudge whatever else is around it. Scaling the far massing up
// is what makes the ring read as a city on the horizon rather than as noise.
const TOWN_FAR_FOOTPRINT = 1.9;
const TOWN_FAR_HEIGHT = 2.6;
const TOWN_COLORS = ['#c0a37c', '#b59770', '#c9ae88', '#ab8c67'];

interface Species {
    file: string;
    count: number;
    /** Target height in metres, before per-instance variation. */
    heightM: number;
    /** Fraction of heightM the size varies by, either way. */
    vary: number;
    innerM: number;
    outerM: number;
    /** Maximum lean off vertical, in degrees. */
    leanDeg: number;
    seed: number;
}

// Empty on purpose: the scene is dressed by hand with the layout tool now, so
// nothing is scattered automatically. The scatter machinery below still works
// and the assets are still shipped in /plants, so adding an entry here brings
// a species back.
//
// If it ever comes back, note the two constraints the old set was tuned to:
// pick species that belong on a Riyadh plateau (the pack's oak, ash, aspen,
// pine and chestnut are temperate and read wrong here), and keep outerM inside
// the tile flattening pad (~224 m). That pad is the only ground guaranteed
// level, and planting is placed at a fixed height, so anything further out
// hovers over or sinks into the real terrain.
const SPECIES: Species[] = [];

/** Deterministic pseudo-random, so the scene is identical every load. */
function makeRandom(seed: number): () => number {
    let s = seed >>> 0;
    return () => {
        s = (s * 1664525 + 1013904223) >>> 0;
        return s / 4294967296;
    };
}

// ---- Noise ---------------------------------------------------------------
function hash2(x: number, y: number): number {
    const n = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
    return n - Math.floor(n);
}

/** Value noise with a smoothstep, in the same family as the cloud shader. */
function valueNoise(x: number, y: number): number {
    const ix = Math.floor(x);
    const iy = Math.floor(y);
    const fx = x - ix;
    const fy = y - iy;
    const ux = fx * fx * (3 - 2 * fx);
    const uy = fy * fy * (3 - 2 * fy);
    const a = hash2(ix, iy);
    const b = hash2(ix + 1, iy);
    const c = hash2(ix, iy + 1);
    const d = hash2(ix + 1, iy + 1);
    return (a * (1 - ux) + b * ux) * (1 - uy) + (c * (1 - ux) + d * ux) * uy;
}

function fbm(x: number, y: number, octaves: number): number {
    let value = 0;
    let amplitude = 0.5;
    let px = x;
    let py = y;
    for (let i = 0; i < octaves; i++) {
        value += amplitude * valueNoise(px, py);
        px *= 2.07;
        py *= 2.03;
        amplitude *= 0.5;
    }
    return value;
}

/**
 * Backdrop relief in metres, for a point given in metres from the site.
 *
 * Only meaningful out past DUNE_INNER_M; it ramps from nothing at the inner rim
 * so the range rises out of the haze instead of starting as a wall.
 */
function backdropRelief(xM: number, zM: number): number {
    const r = Math.hypot(xM, zM);
    const ramp = THREE.MathUtils.smoothstep(r, DUNE_INNER_M, DUNE_INNER_M + DUNE_RAMP_M);
    if (ramp <= 0) return 0;

    // Along-wind and across-wind coordinates: crests run perpendicular to the
    // wind, so the noise is stretched along it.
    const cos = Math.cos(DUNE_BEARING_RAD);
    const sin = Math.sin(DUNE_BEARING_RAD);
    const along = (xM * cos + zM * sin) / 1000;
    const across = (-xM * sin + zM * cos) / 1000;

    // Big, soft bodies only. This is read as a silhouette from kilometres away,
    // so fine detail is wasted vertices, and a crisp ridge wave at this scale
    // came out as parallel banding across the whole horizon.
    const body = fbm(along * 0.16, across * 0.42, 4);
    const swell = Math.sin((across * 0.55 + body * 3.2) * Math.PI) * 0.5 + 0.5;
    return ramp * (body * 0.78 + swell * 0.22) * DUNE_RELIEF_M;
}

export interface DesertDecor {
    readonly group: THREE.Group;
    /**
     * The near-field planting alone. It stands on the PLOT, not on the earth,
     * so the host reparents it into the model-alignment group: when the model
     * is nudged or rotated to fit the terrain, the groves must ride along
     * while the dunes and the distant town stay glued to the ground.
     */
    readonly plantingGroup: THREE.Group;
    setVisible(visible: boolean): void;
    /** Drives the deferred house placement; safe to call every frame. */
    update(dt: number): void;
    /**
     * Drop the heights the desert houses are standing on and sample again.
     *
     * Called when the terrain itself moves: the tiles level themselves as they
     * stream, and a compound seated before a correction keeps a world height
     * that no longer matches the ground under it, which buries the settlement
     * or leaves it hanging.
     */
    resetTownSeating(): void;
    /**
     * Tie the distant range to the scene's haze. Called whenever the sun moves,
     * so the backdrop is never a fixed colour sitting against a changing sky.
     */
    setHaze(hazeColor: THREE.Color): void;
    setDaylight(daylight: number): void;
    dispose(): void;
}

export function createDesertDecor(opts: {
    scene: THREE.Scene;
    /** Directory the species files live in, with trailing slash. */
    baseUrl: string;
    /** World Y of the grade the desert sits at. */
    groundY: number;
    /** Half-extent of the masterplan in metres, to keep dressing off the plot. */
    plotRadiusM: number;
    /**
     * Real ground height at a point, in world units, or null if the terrain is
     * not loaded there yet. Used to seat the houses; without it they would sit
     * at a flat assumed grade and float over the photoreal ground.
     */
    sampleGround: (xM: number, zM: number) => number | null;
}): DesertDecor {
    const { scene, baseUrl, groundY, plotRadiusM, sampleGround } = opts;

    const group = new THREE.Group();
    group.name = 'desert-decor';
    scene.add(group);
    const plantingGroup = new THREE.Group();
    plantingGroup.name = 'plot-planting';
    group.add(plantingGroup);

    const disposables: (THREE.BufferGeometry | THREE.Material)[] = [];

    // ---- Distant dune range ----------------------------------------------
    const ring = new THREE.RingGeometry(
        m(DUNE_INNER_M),
        m(DUNE_OUTER_M),
        DUNE_RADIAL_SEGMENTS,
        DUNE_RINGS,
    );
    ring.rotateX(-Math.PI / 2);
    const ringPos = ring.attributes.position as THREE.BufferAttribute;
    const perMetre = sceneScale.unitsPerMeter;
    for (let i = 0; i < ringPos.count; i++) {
        const xM = ringPos.getX(i) / perMetre;
        const zM = ringPos.getZ(i) / perMetre;
        ringPos.setY(i, m(backdropRelief(xM, zM)));
    }
    ring.computeVertexNormals();

    // Unlit on purpose. At this distance a lit surface contributes no shading
    // worth having, and it would drift out of step with the sky as the sun
    // moves; driving one flat tone from the scene's own haze keeps the range
    // welded to the horizon at every hour instead.
    const backdropMaterial = new THREE.MeshBasicMaterial({
        color: new THREE.Color('#c4b394'),
        fog: false,
        depthWrite: true,
    });
    const backdrop = new THREE.Mesh(ring, backdropMaterial);
    backdrop.position.y = groundY;
    backdrop.frustumCulled = false;
    backdrop.renderOrder = -2;
    group.add(backdrop);
    disposables.push(ring, backdropMaterial);

    // ---- Desert houses ---------------------------------------------------
    // Built now, seated later. The terrain streams in progressively, so a
    // raycast at construction time finds nothing and every house would land on
    // an assumed flat grade, which is precisely the floating-cubes-on-imagery
    // failure this is meant to avoid. Placement retries until the ground answers.
    const townRnd = makeRandom(5150);
    const townBox = new THREE.BoxGeometry(1, 1, 1);
    townBox.translate(0, 0.5, 0); // pivot at the base
    const townMaterial = new THREE.MeshStandardMaterial({ roughness: 0.92, metalness: 0 });
    const townCount = TOWN_CLUSTERS * TOWN_BLOCKS_PER_CLUSTER;
    const town = new THREE.InstancedMesh(townBox, townMaterial, townCount);
    town.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(townCount * 3), 3);
    town.castShadow = false;
    town.receiveShadow = true;
    town.count = 0; // nothing drawn until it has real ground to stand on
    town.frustumCulled = false;
    group.add(town);
    disposables.push(townBox, townMaterial);

    interface Block {
        xM: number;
        zM: number;
        widthM: number;
        depthM: number;
        heightM: number;
        rotation: number;
        colour: THREE.Color;
    }
    // groundY is filled in by the first retry that finds terrain under this
    // compound and then never re-sampled. The tiles stream in and out, so a
    // later pass regularly sees less ground than an earlier one; re-sampling
    // every pass and rewriting the buffer from zero made the settlement shrink
    // from 260 houses to 104 as tiles unloaded.
    //
    // exact says whether that height came from a real raycast or was borrowed
    // from the nearest compound that had one. Google only keeps tiles loaded
    // where the camera is looking, so a third of the ring never gets a hit at
    // all; before the fallback those compounds simply never appeared.
    interface Cluster {
        xM: number;
        zM: number;
        blocks: Block[];
        groundY: number | null;
        exact: boolean;
    }
    const clusters: Cluster[] = [];
    for (let c = 0; c < TOWN_CLUSTERS; c++) {
        const angle = townRnd() * Math.PI * 2;
        const radiusM = TOWN_INNER_M + townRnd() * (TOWN_OUTER_M - TOWN_INNER_M);
        const cx = Math.cos(angle) * radiusM;
        const cz = Math.sin(angle) * radiusM;
        // A compound shares one orientation, which is what makes it read as a
        // settlement rather than as loose boxes.
        const facing = townRnd() * Math.PI * 2;
        // 0 on the near ring, 1 on the far one.
        const far = THREE.MathUtils.smoothstep(radiusM, TOWN_INNER_M, TOWN_OUTER_M);
        const footprint = 1 + far * TOWN_FAR_FOOTPRINT;
        const rise = 1 + far * TOWN_FAR_HEIGHT;
        const spreadM = (22 + townRnd() * 38) * footprint;
        const blocks: Block[] = [];
        for (let b = 0; b < TOWN_BLOCKS_PER_CLUSTER; b++) {
            blocks.push({
                xM: cx + (townRnd() - 0.5) * spreadM * 2,
                zM: cz + (townRnd() - 0.5) * spreadM * 2,
                widthM: (7 + townRnd() * 15) * footprint,
                depthM: (7 + townRnd() * 13) * footprint,
                heightM: (3.5 + townRnd() * 6.5) * rise,
                rotation: facing + (townRnd() - 0.5) * 0.5,
                colour: new THREE.Color(TOWN_COLORS[Math.floor(townRnd() * TOWN_COLORS.length)]),
            });
        }
        clusters.push({ xM: cx, zM: cz, blocks, groundY: null, exact: false });
    }

    const townMatrix = new THREE.Matrix4();
    const townScale = new THREE.Vector3();
    let townSeated = false;
    let townTimer = 0;

    let lastMeasured = -1;

    const seatTown = () => {
        let written = 0;
        let missed = 0;

        for (const cluster of clusters) {
            if (cluster.exact) continue;
            // One sample per compound, not per block: a settlement sits on a
            // terrace, and sampling each box individually makes the cluster
            // ripple over every bump in the terrain.
            const ground = sampleGround(cluster.xM, cluster.zM);
            if (ground === null) {
                missed++;
                continue;
            }
            cluster.groundY = ground;
            cluster.exact = true;
        }

        // Borrow a grade for whatever the tiles would not answer for. Nearest
        // measured compound rather than a global average: the site runs down
        // into a wadi, so one flat number would bury half the ring and float
        // the other half.
        for (const cluster of clusters) {
            if (cluster.exact) continue;
            let best: Cluster | null = null;
            let bestDistance = Infinity;
            for (const other of clusters) {
                if (!other.exact) continue;
                const dx = other.xM - cluster.xM;
                const dz = other.zM - cluster.zM;
                const distance = dx * dx + dz * dz;
                if (distance < bestDistance) {
                    bestDistance = distance;
                    best = other;
                }
            }
            if (best) cluster.groundY = best.groundY;
        }

        for (const cluster of clusters) {
            const ground = cluster.groundY;
            if (ground === null) continue;
            for (const b of cluster.blocks) {
                townMatrix.makeRotationY(b.rotation);
                townScale.set(m(b.widthM), m(b.heightM), m(b.depthM));
                townMatrix.scale(townScale);
                townMatrix.setPosition(m(b.xM), ground, m(b.zM));
                town.setMatrixAt(written, townMatrix);
                town.setColorAt(written, b.colour);
                written++;
            }
        }
        town.count = written;
        town.instanceMatrix.needsUpdate = true;
        if (town.instanceColor) town.instanceColor.needsUpdate = true;
        // Settled once most compounds found ground; the rest are off the edge of
        // what has streamed in and would only ever miss.
        if (missed <= clusters.length * 0.25) townSeated = true;
        // Only on change: this retries every 1.5 s for up to 30 tries, and
        // logging every pass buried everything else in the console.
        const measured = clusters.filter((c) => c.exact).length;
        if (measured === lastMeasured) return;
        lastMeasured = measured;
        const borrowed = clusters.filter((c) => !c.exact && c.groundY !== null).length;
        console.info(
            `[decor] seated ${written} houses across ${measured + borrowed} compounds ` +
                `(${measured} on measured terrain, ${borrowed} on a borrowed grade)`,
        );
    };

    // ---- Planting --------------------------------------------------------
    const loader = new GLTFLoader();
    const draco = new DRACOLoader();
    draco.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
    loader.setDRACOLoader(draco);

    let plantTotal = 0;
    for (const species of SPECIES) {
        loader.load(
            baseUrl + species.file,
            (gltf) => {
                let source: THREE.Mesh | null = null;
                gltf.scene.traverse((child) => {
                    const mesh = child as THREE.Mesh;
                    if (!source && mesh.isMesh) source = mesh;
                });
                if (!source) return;
                const src = source as THREE.Mesh;

                // Normalise the asset instead of trusting its authoring.
                //
                // These agree on nothing: cypress is centred on the origin,
                // banana_plant sits base-at-origin, and native heights run from
                // 1 to 5 units. Rebasing the geometry once means the placement
                // below is identical for every species and a third can be
                // dropped in without measuring it first.
                const geometry = src.geometry.clone();
                geometry.computeBoundingBox();
                const bb = geometry.boundingBox!;
                const nativeHeight = Math.max(bb.max.y - bb.min.y, 1e-6);
                geometry.translate(
                    -(bb.min.x + bb.max.x) / 2,
                    -bb.min.y,
                    -(bb.min.z + bb.max.z) / 2,
                );

                const instances = new THREE.InstancedMesh(geometry, src.material, species.count);
                const rnd = makeRandom(species.seed);
                const q = new THREE.Quaternion();
                const euler = new THREE.Euler();
                const scale = new THREE.Vector3();
                const position = new THREE.Vector3();
                const local = new THREE.Matrix4();

                let placed = 0;
                let guard = 0;
                while (placed < species.count && guard++ < species.count * 20) {
                    const angle = rnd() * Math.PI * 2;
                    // Biased inward, so planting thins with distance the way an
                    // oasis does rather than tiling a field.
                    const t = Math.pow(rnd(), 1.7);
                    const radiusM = species.innerM + t * (species.outerM - species.innerM);
                    const xM = Math.cos(angle) * radiusM;
                    const zM = Math.sin(angle) * radiusM;
                    // Clear of the plot's own footprint, which is planted already.
                    if (Math.abs(xM) < plotRadiusM * 0.98 && Math.abs(zM) < plotRadiusM * 0.42) {
                        continue;
                    }

                    const heightM = species.heightM * (1 - species.vary + rnd() * species.vary * 2);
                    const s = m(heightM) / nativeHeight;
                    // Leaning and tapered. Nothing in a grove is a plumb
                    // cylinder, and a field of identical uprights is exactly
                    // what gives instancing away, so every one gets its own
                    // lean, twist and girth.
                    const lean = THREE.MathUtils.degToRad(species.leanDeg);
                    euler.set(-lean + rnd() * lean * 2, rnd() * Math.PI * 2, -lean + rnd() * lean * 2);
                    q.setFromEuler(euler);
                    scale.set(s * (0.86 + rnd() * 0.28), s, s * (0.86 + rnd() * 0.28));
                    // Stands on the grade, because the geometry is rebased.
                    position.set(m(xM), groundY, m(zM));
                    local.compose(position, q, scale);
                    instances.setMatrixAt(placed, local);
                    placed++;
                }
                instances.count = placed;
                instances.instanceMatrix.needsUpdate = true;
                // Out of the shadow pass: the foliage is alpha-cut cards, and
                // they stipple the plot when they cast.
                instances.castShadow = false;
                instances.receiveShadow = false;
                instances.name = `planting-${species.file}`;
                plantingGroup.add(instances);

                plantTotal += placed;
                console.info(
                    `[decor] ${placed} x ${species.file} in one draw call (${plantTotal} plants total)`,
                );
            },
            undefined,
            (err) => console.error(`Failed to load ${species.file}:`, err),
        );
    }

    const hazeTone = new THREE.Color();
    return {
        group,
        plantingGroup,
        setVisible(visible) {
            group.visible = visible;
            // The planting may live under the alignment group by the time this
            // is called; toggle it explicitly so reparenting cannot orphan it.
            plantingGroup.visible = visible;
        },
        resetTownSeating() {
            for (const cluster of clusters) {
                cluster.exact = false;
                cluster.groundY = null;
            }
            townSeated = false;
            townTimer = 0;
            lastMeasured = -1;
        },
        update(dt) {
            // Retry until settled, with no give-up: the cap this used to have
            // (30 tries, ~45 s) raced Google's CDN on a cold cache — the first
            // load at freshly changed coordinates streamed tiles slower than
            // the window, the cap tripped, and the town never appeared until a
            // reload. A pass is ~190 raycasts every 1.5 s, cheap enough to
            // just keep asking until the terrain answers.
            if (townSeated) return;
            townTimer -= dt;
            if (townTimer > 0) return;
            townTimer = 1.5;
            seatTown();
        },
        setDaylight(daylight) {
            // The houses are lit geometry standing on tiles that get their own
            // night gain multiplied in. Without matching that fall-off they stay
            // bright against dark terrain after sunset and read as glowing
            // boxes rather than a village.
            const level = THREE.MathUtils.lerp(0.4, 1, THREE.MathUtils.clamp(daylight, 0, 1));
            townMaterial.color.setScalar(level);
        },
        setHaze(hazeColor) {
            // A shade deeper than the haze, never a different hue: the range has
            // to separate from the sky by value alone or it stops looking like
            // distance and starts looking like a painted flat.
            hazeTone.copy(hazeColor).multiplyScalar(1 - BACKDROP_CONTRAST);
            backdropMaterial.color.copy(hazeTone);
        },
        dispose() {
            scene.remove(group);
            plantingGroup.removeFromParent();
            disposables.forEach((d) => d.dispose());
            group.traverse((child) => {
                const mesh = child as THREE.InstancedMesh;
                if (mesh.isInstancedMesh) mesh.geometry.dispose();
            });
            draco.dispose();
        },
    };
}
